import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getOrderDetail } from '../orderDetailActions';
import { getAccountDetailsApi } from '../../../../api/order';
import logo from '../../../../assets/images/logo_new.png'
import './taxInvoicePage.scss';

const TaxInvoicePage = (props) => {
    const dispatch = useDispatch();
    const { pageLoading, orderDetail } = useSelector(
        (state) => state.orderDetail
    );
    const [billingInfo, setBillingInfo] = useState({});

    const { orderId } = useParams();

    useEffect(() => {
        if (orderId) {
            dispatch(getOrderDetail(orderId, true));
            getAccountDetailsApi().then((res) => {
                setBillingInfo(res.result);
            });
        }
    }, [orderId]);

    useEffect(() => {
        if (orderDetail && billingInfo) {
            requestAnimationFrame(() => {
                setTimeout(() => {
                    window.print();
                }, 500);
            });
        }
    }, [orderDetail, billingInfo]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD'
        }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-AU', {
            day: 'numeric',
            month: 'long',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
    };

    const calculateGST = (amount) => {
        return amount / 11;
    };

    const formatGST = (amount) => {
        return formatCurrency(calculateGST(amount));
    };

    if (!orderDetail) {
        return null;
    }

    const invoiceNumber = orderDetail.increment_id
    const invoiceDate = formatDate(orderDetail.order_date);

    const getGTS = (orders) => {
        const grandTotal = getGrandTotal(orders);
        const processingFee = getProcessingFee(orders);
        const price = grandTotal + processingFee;
        return formatCurrency(price / 11)
    };

    const getGrandTotal = (orders) => {
        let grandTotal = 0;
        orders.items.forEach((item) => {
            grandTotal += item.sub_total_inc_shipping;
        });
        return grandTotal;
    }

    const getProcessingFee = (orders) => {
        return orders.processing_fee;
    };

    return (
        <div className="tax-invoice-page">
            {/* Tax Invoice Header */}
            <div className="tax-invoice-header">
                <div className="invoice-info">
                    <h1 className="invoice-title">Tax Invoice</h1>
                    <div className="invoice-info-header">Invoice No: <sapn className="invoice-number"></sapn>{invoiceNumber}</div>
                    <div className="invoice-info-header">Invoice Date: <sapn className="invoice-number"></sapn>{invoiceDate}</div>
                </div>
                <div className="header-logo">
                    <img
                        src={logo}
                        className="logo"
                        alt="DROPSHIPZONE"
                    />
                </div>
            </div>

            <div className="tax-invoice-content">
                {/* Two Column Layout - Invoice To & From */}
                <div className="col2-set">
                    <div className="column-1">
                        <div className='detail-header'>Invoice To</div>
                        <address>
                            {billingInfo.firstName + ' ' + billingInfo.lastName}<br />
                            ABN: {billingInfo.abn}<br />
                            {billingInfo.street}<br />
                            {billingInfo.suburb + ' ' + billingInfo.state + ' ' + billingInfo.postcode}<br />
                            Australia
                        </address>
                    </div>
                    <div className="column-2">
                        <div className='detail-header'>Invoice From</div>
                        <address>
                            New Aim PTY LTD<br />
                            ABN: 50 115 804 432<br />
                            16-18 CATO ST<br />
                            HAWTHORN EAST VIC 3123<br />
                            Australia
                        </address>
                    </div>
                </div>

                {/* Items Table */}
                <div className="items-section">
                    <div className="table-container">
                        <table className="data-table">
                            <thead>
                                <tr>
                                    <th>Dropshipzone Order ID</th>
                                    <th>Product Name</th>
                                    <th>SKU</th>
                                    <th className="a-right">Unit Price</th>
                                    <th className="a-right">Shipping Per Item</th>
                                    <th className="a-right">Qty</th>
                                    <th className="a-right">Amount AUD (incl. GST)</th>
                                    <th className="a-right">GST</th>
                                    <th className="a-right">Order Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {orderDetail.items && orderDetail.items.map((item, index) => (
                                    <tr key={index}>
                                        <td>
                                            {index == 0 && <span className="item-data-text">{orderDetail.increment_id}</span>}
                                        </td>
                                        <td className="product-name-cell">
                                            <span className="item-data-text product-name-text">{item.name}</span>
                                        </td>
                                        <td className="a-left">
                                            <span className="item-data-text">{item.sku}</span>
                                        </td>
                                        <td className="a-right">
                                            <span className="item-data-text">{formatCurrency(item.price)}</span>
                                        </td>
                                        <td className="a-right">
                                            <span className="item-data-text">{formatCurrency(item.shipping_fee)}</span>
                                        </td>
                                        <td className="a-right">
                                            <span className="item-data-text">{item.qty}</span>
                                        </td>
                                        <td className="a-right">
                                            <span className="item-data-text">{formatCurrency(item.sub_total_inc_shipping)}</span>
                                        </td>
                                        <td className="a-right">
                                            <span className="item-data-text">{formatGST(item.sub_total_inc_shipping)}</span>
                                        </td>
                                        <td className="a-right">
                                            <span className="item-data-text">{formatDateTime(orderDetail.order_date)}</span>
                                        </td>
                                    </tr>
                                ))}
                                {/* Processing fee row */}
                                {orderDetail.processing_fee > 0 && (
                                    <tr>
                                        <td></td>
                                        <td>
                                            <span className="item-data-text">Processing fee - PayPal</span>
                                        </td>
                                        <td></td>
                                        <td className="a-right">
                                            <span className="item-data-text">{formatCurrency(orderDetail.processing_fee)}</span>
                                        </td>
                                        <td></td>
                                        <td className="a-right">
                                            <span className="item-data-text">1</span>
                                        </td>
                                        <td className="a-right">
                                            <span className="item-data-text">{formatCurrency(orderDetail.processing_fee)}</span>
                                        </td>
                                        <td className="a-right">
                                            <span className="item-data-text">{formatGST(orderDetail.processing_fee)}</span>
                                        </td>
                                        <td className="a-right">
                                            <span className="item-data-text">{formatDateTime(orderDetail.order_date)}</span>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                            <tfoot>
                                <tr className="price-summary-text">
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td className="a-left nobr">Grand Total (incl. GST)</td>
                                    <td></td>
                                    <td></td>
                                    <td className="a-right">{formatCurrency(getGrandTotal(orderDetail) + getProcessingFee(orderDetail))}</td>
                                </tr>
                                <tr className="price-summary-text">
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td></td>
                                    <td className="a-left nobr">GST</td>
                                    <td></td>
                                    <td></td>
                                    <td className="a-right">{getGTS(orderDetail)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="buttons-set">
                    <button
                        type="button"
                        title="Close Window"
                        className="button close-button"
                        onClick={() => window.close()}
                    >
                        <span>Close Window</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default TaxInvoicePage;
